import { Alert, Descriptions, Drawer, Typography } from "antd"
import type { MetricDim } from "@csbi/contracts"
import { catalog, type MetricDefinition } from "@csbi/metric-catalog"
import { TileWidget } from "@csbi/widgets"
import type { ThemeMode } from "@csbi/viz-core"
import { useFilterStore } from "../../filters/model/filter-store.js"
import { useTileQuery, type TileSpec } from "../hooks/use-tile-query.js"
import { drillSpecFor, filterToCategory } from "../drill.js"

const DEFINITIONS: ReadonlyMap<string, MetricDefinition> = new Map(catalog.map((m) => [m.id, m]))

export type DrillDrawerProps = {
  /** The chart's own spec — the drill query is derived from it, never assembled separately */
  spec: TileSpec
  dim: MetricDim
  category: string | null
  chartTitle: string
  mode: ThemeMode
  categoryLabel?: (raw: string) => string
  user?: { name: string; email?: string; role?: string }
  onClose: () => void
}

export function DrillDrawer({ spec, dim, category, chartTitle, mode, categoryLabel, user, onClose }: DrillDrawerProps) {
  const filters = useFilterStore((s) => s.filters)
  const open = category != null
  // `enabled` keeps a closed drawer from fetching — a dozen tiles on a page would otherwise each hold a second query
  const query = useTileQuery(drillSpecFor(spec, dim), filters, open)

  const full = query.data
  const filtered = full && category != null ? filterToCategory(full, dim, category) : undefined

  /**
   * ⚠ **An empty result after a successful query is reported, not drawn.**
   */
  const matchedNothing = Boolean(filtered && full?.state === "ok" && filtered.rows.length === 0)
  const shown = category == null ? "" : (categoryLabel?.(category) ?? category)

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={720}
      destroyOnHidden
      title={
        <span>
          {chartTitle} · <Typography.Text strong>{shown}</Typography.Text>
        </span>
      }
    >
      {dim === "user" && user && (
        <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Name">{user.name}</Descriptions.Item>
          {user.email && <Descriptions.Item label="Email">{user.email}</Descriptions.Item>}
          {user.role && <Descriptions.Item label="Assigned role">{user.role}</Descriptions.Item>}
        </Descriptions>
      )}

      {dim === "user" && !user && categoryLabel && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="This user has been deleted"
          description="Their activity is still in the customer's logs and is counted here, but the account itself is gone, so there is no name, email or role to show."
        />
      )}

      <p style={{ marginTop: 0, color: "var(--csbi-ink-secondary)", fontSize: 13 }}>
        How <Typography.Text code>{shown}</Typography.Text> moved over the selected period. The filters at the
        top of the page apply here too.
      </p>

      {matchedNothing ? (
        <Alert
          type="warning"
          showIcon
          message={`No rows for "${shown}" in this period`}
          description="The chart groups the whole range, so a category can appear there while every individual period is empty. Widening the range, or switching granularity, usually shows where it came from."
        />
      ) : (
        <div style={{ height: 320 }}>
          <TileWidget
            title={`${chartTitle} over time`}
            widget="bar"
            {...(filtered ? { result: filtered } : {})}
            definitions={DEFINITIONS}
            mode={mode}
            height="100%"
            {...(query.isLoading ? { loading: true } : {})}
            {...(query.errorMessage ? { error: query.errorMessage } : {})}
            // No drill from inside a drill: the panel is already the two-way view, and a second level would have no axis left
            actions={false}
          />
        </div>
      )}
    </Drawer>
  )
}
